import React from "react";
import { formatLongDisplayDate } from "../utils";

const InputLabel = ({ children }) => (
  <p className="mb-0.5 sm:mb-1 text-[9px] sm:text-[10px] font-bold uppercase tracking-[0.12em] sm:tracking-[0.14em] text-slate-400">
    {children}
  </p>
);

const UserBookingForm = ({
  summaryRef,
  section,
  selectedSeat,
  bookingForm,
  onSubmit,
  hasExistingBooking,
}) => {
  const bookingDate = bookingForm.dates[0] || "";
  const isSubmitDisabled = !selectedSeat || hasExistingBooking;
  const equipmentLabel = selectedSeat
    ? `${selectedSeat.type.toUpperCase()} ONLY`
    : `${bookingForm.requirement.toUpperCase()} ONLY`;

  return (
    <form
      ref={summaryRef}
      onSubmit={onSubmit}
      className="flex h-auto lg:h-full flex-col"
    >
      <h3 className="text-base sm:text-lg lg:text-xl font-medium tracking-[-0.03em] text-slate-900">
        Booking Summary
      </h3>

      <div className="mt-3 sm:mt-4 lg:mt-5  border border-slate-200 bg-white p-3 sm:p-4 lg:p-5 shadow-[0_6px_20px_rgba(15,23,42,0.04)]">
        <p
          className={`text-[9px] sm:text-[12px] font-medium tracking-[0.12em] sm:tracking-[0.12em] ${
            selectedSeat ? "text-emerald-500" : "text-slate-400"
          }`}
        >
          {selectedSeat ? "Available to Reserve" : "Select a Seat"}
        </p>

        <div className="mt-1 sm:mt-1.5">
          {selectedSeat ? (
            <p className="text-[14px] sm:text-lg lg:text-xl font-medium tracking-[-0.03em] text-slate-900">
              Seat {selectedSeat.label}
            </p>
          ) : (
            <div className="mt-2 sm:mt-3 flex flex-col items-center text-center py-2 sm:py-4">
              <div className="flex h-10 w-10 sm:h-12 sm:w-12 lg:h-14 lg:w-14 items-center justify-center rounded-xl sm:rounded-2xl bg-slate-100 mb-2 sm:mb-3 lg:mb-4">
                <svg
                  className="h-4 w-4 sm:h-5 sm:w-5 lg:h-6 lg:w-6 text-slate-400"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <rect x="4" y="4" width="16" height="16" rx="3" />
                  <path d="M9 9h6M9 13h4" />
                </svg>
              </div>
              <p className="text-xs sm:text-sm font-bold text-slate-700">
                No seat selected
              </p>
              <p className="mt-1 sm:mt-1.5 text-[10px] sm:text-xs leading-4 sm:leading-5 text-slate-400">
                Select an available seat from the grid to review booking details
                here.
              </p>
            </div>
          )}
        </div>

        {selectedSeat && (
          <div className="mt-3 sm:mt-4 lg:mt-5 space-y-2 sm:space-y-3 lg:space-y-3.5">
            {/* Compact 2-column layout on mobile, stacked on desktop */}
            <div className="grid grid-cols-2 lg:grid-cols-1 gap-2 sm:gap-3 lg:gap-3.5">
              <div>
                <InputLabel>Seat ID</InputLabel>
                <p className="text-xs sm:text-sm font-medium text-slate-900">
                  {selectedSeat.label}
                </p>
              </div>

              <div>
                <InputLabel>Date</InputLabel>
                <p className="text-xs sm:text-sm font-medium text-slate-900">
                  {formatLongDisplayDate(bookingDate)}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-1 gap-2 sm:gap-3 lg:gap-3.5">
              <div>
                <InputLabel>Equipment</InputLabel>
                <span className="inline-flex rounded-md sm:rounded-lg border border-slate-200 bg-slate-50 px-2 py-0.5 sm:px-2.5 sm:py-1 text-[9px] sm:text-[10px] font-semibold uppercase tracking-[0.04em] sm:tracking-[0.06em] text-slate-900">
                  {equipmentLabel}
                </span>
              </div>

              <div>
                <InputLabel>Section</InputLabel>
                <p className="text-xs sm:text-sm font-medium text-slate-900">
                  {section.name}
                </p>
              </div>
            </div>
          </div>
        )}

      </div>

      {hasExistingBooking && (
        <div className="mt-4 flex items-center gap-1.5 sm:gap-2 rounded-lg sm:rounded-xl border border-rose-100 bg-rose-50 p-2 sm:p-2.5 text-[10px] sm:text-[11px] font-semibold text-rose-600">
          <svg
            className="h-3 w-3 sm:h-3.5 sm:w-3.5 shrink-0"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="12" cy="12" r="10" />
            <line x1="12" x2="12" y1="8" y2="12" />
            <line x1="12" x2="12.01" y1="16" y2="16" />
          </svg>
          <span>Booking already exists for the day</span>
        </div>
      )}

      <button
        type="submit"
        disabled={isSubmitDisabled}
        className={`mt-4 sm:mt-5 lg:mt-6 flex h-11 sm:h-12 lg:h-14 w-full items-center justify-center gap-2 sm:gap-2.5 rounded-xl sm:rounded-2xl text-[10px] sm:text-[11px] lg:text-xs font-black uppercase tracking-[0.12em] sm:tracking-[0.14em] transition-all duration-200 ${
          isSubmitDisabled
            ? "cursor-not-allowed bg-slate-200 text-slate-500"
            : "bg-black text-white shadow-[0_16px_32px_rgba(0,0,0,0.16)] hover:bg-slate-900 hover:shadow-[0_20px_40px_rgba(0,0,0,0.20)] active:scale-[0.98]"
        }`}
      >
        <span>Reserve Seat</span>
        <svg
          className="w-6 h-6 text-current"
          aria-hidden="true"
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          fill="none"
          viewBox="0 0 24 24"
        >
          <path
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M8.5 11.5 11 14l4-4m6 2a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
          />
        </svg>
      </button>
    </form>
  );
};

export default UserBookingForm;
